import "react-toastify/dist/ReactToastify.css";
import { useEffect } from "react";
import { useRoutes } from "react-router-dom";
import { routes } from "./router/routes";
import { ToastContainer } from "react-toastify";
import { ConfigProvider } from "antd";
import { useTranslation } from "react-i18next";
import en_US from 'antd/locale/en_US';
import fr_FR from 'antd/locale/fr_FR';
import ar_EG from 'antd/locale/ar_EG';
import dayjs from 'dayjs';
import 'dayjs/locale/fr';
import 'dayjs/locale/ar';
import { RTL_LANGUAGES } from "./constants/languages";

const ANTD_LOCALES: Record<string, typeof en_US> = {
  en: en_US,
  fr: fr_FR,
  ar: ar_EG,
};

function App() {
  const content = useRoutes(routes);
  const { i18n } = useTranslation();

  const language = i18n.language || "en";
  // i18next hands back regional tags too ("ar-DZ"), so key off the base
  // subtag -- an exact lookup would silently fall back to English for those.
  const baseLanguage = language.split("-")[0];
  const currentLocale = ANTD_LOCALES[baseLanguage] ?? en_US;
  const isRtl = RTL_LANGUAGES.includes(baseLanguage);

  dayjs.locale(baseLanguage);

  // Ant Design's `direction` only reaches components it renders. The page's own
  // markup and stylesheets read dir/lang off <html>, so both have to be set for
  // Arabic to mirror the whole portal rather than just the widgets.
  useEffect(() => {
    document.documentElement.setAttribute("dir", isRtl ? "rtl" : "ltr");
    document.documentElement.setAttribute("lang", baseLanguage);
  }, [isRtl, baseLanguage]);

  return (
    <ConfigProvider
      locale={currentLocale}
      direction={isRtl ? "rtl" : "ltr"}
      theme={{
        token: {
          fontFamily: "IBM Plex Sans, IBM Plex Sans Arabic, sans-serif",
          colorPrimary: "#4554fa",
          colorInfo: "#4554fa",
          colorLink: "#4554fa",
        },
      }}
    >
      {content}

      <ToastContainer
        position={isRtl ? "top-left" : "top-right"}
        autoClose={2000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={isRtl}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="colored"
      />
    </ConfigProvider>
  );
}

export default App;
